import { useCallback, useRef, useState } from 'react';
import { Table, Button, Space, Modal, Input, message, Tag } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useWisecureMutation, useInvalidateQueries, useWisecureClient } from '@wisecure/api-client/hooks';
import { DocumentStatusTag } from './DocumentStatusTag';
import { queryKeys } from '@/api/query-keys';
import { useAuthStore } from '@/stores';
import { getDocumentTypeLabel } from '@/utils/document-type-labels';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif'];

function getFileExtension(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const last = pathname.split('/').pop() ?? '';
    const dot = last.lastIndexOf('.');
    return dot >= 0 ? last.substring(dot + 1).toLowerCase() : '';
  } catch {
    return '';
  }
}

function isImage(url: string): boolean {
  return IMAGE_EXTENSIONS.includes(getFileExtension(url));
}

function isPdf(url: string): boolean {
  return getFileExtension(url) === 'pdf';
}

type PreviewType = 'image' | 'pdf' | 'other';

function getPreviewType(url: string): PreviewType {
  if (isImage(url)) return 'image';
  if (isPdf(url)) return 'pdf';
  return 'other';
}

export interface DocumentItem {
  id: string;
  type: string;
  status: string;
  url?: string;
  statusDescription?: string;
  entityType: string;
  entityId: string;
  createdAt: string;
}

interface DocumentListProps {
  documents: DocumentItem[];
  isLoading?: boolean;
  entityType: string;
  entityId: string;
  onValidated?: () => void;
}

export function DocumentList({ documents, isLoading, entityType, entityId, onValidated }: DocumentListProps) {
  const invalidate = useInvalidateQueries();
  const client = useWisecureClient();
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [denyDocId, setDenyDocId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const [previewDisplayUrl, setPreviewDisplayUrl] = useState<string | null>(null);
  const [previewOriginalUrl, setPreviewOriginalUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<PreviewType>('other');
  const pdfBlobUrlRef = useRef<string | null>(null);

  const revokePdfBlobUrl = useCallback(() => {
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = null;
    }
  }, []);

  const validateMutation = useWisecureMutation(
    (client, { id, status, reason }: { id: string; status: string; reason?: string }) =>
      client.documents.documentsControllerValidate({
        id,
        requestBody: { status: status as 'VALIDATED' | 'DENIED', statusDescription: reason },
      }),
    {
      onSuccess: () => {
        message.success('Document mis à jour');
        invalidate(queryKeys.documents(entityType, entityId));
        onValidated?.();
      },
      onError: () => message.error('Erreur lors de la validation du document'),
    },
  );

  const batchValidateMutation = useWisecureMutation(
    (client, validations: { documentId: string; status: string; reason?: string }[]) =>
      client.documents.documentsControllerBatchValidate({
        requestBody: { validations: validations.map((v) => ({ documentId: v.documentId, status: v.status as 'VALIDATED' | 'DENIED', reason: v.reason })) },
      }),
    {
      onSuccess: () => {
        message.success('Documents mis à jour');
        setSelectedRowKeys([]);
        invalidate(queryKeys.documents(entityType, entityId));
        onValidated?.();
      },
      onError: () => message.error('Erreur lors de la validation groupée'),
    },
  );

  const handleValidate = (id: string) => {
    validateMutation.mutate({ id, status: 'VALIDATED' });
  };

  const handleDeny = () => {
    if (denyDocId) {
      validateMutation.mutate({ id: denyDocId, status: 'DENIED', reason: denyReason });
    }
    setDenyModalOpen(false);
    setDenyReason('');
    setDenyDocId(null);
  };

  const handleBatchValidate = () => {
    const validations = selectedRowKeys.map((key) => ({
      documentId: key as string,
      status: 'VALIDATED',
    }));
    batchValidateMutation.mutate(validations);
  };

  const handleView = async (docId: string) => {
    setLoadingDocId(docId);
    try {
      const result = await client.documents.documentsControllerGetSignedUrl({ id: docId });
      if (result.url) {
        const type = getPreviewType(result.url);
        setPreviewType(type);
        setPreviewOriginalUrl(result.url);

        if (type === 'pdf') {
          revokePdfBlobUrl();
          const token = useAuthStore.getState().token;
          const baseUrl = import.meta.env.VITE_API_URL ?? '';
          const resp = await fetch(`${baseUrl}/api/documents/${docId}/content`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          const blobUrl = URL.createObjectURL(blob);
          pdfBlobUrlRef.current = blobUrl;
          setPreviewDisplayUrl(blobUrl);
        } else {
          setPreviewDisplayUrl(result.url);
        }
      } else {
        message.error('Impossible d\'obtenir le lien du document');
      }
    } catch {
      message.error('Impossible d\'obtenir le lien du document');
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleClosePreview = () => {
    revokePdfBlobUrl();
    setPreviewDisplayUrl(null);
    setPreviewOriginalUrl(null);
    setPreviewType('other');
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag>{getDocumentTypeLabel(type)}</Tag>,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <DocumentStatusTag status={status} />,
    },
    {
      title: 'Lien',
      dataIndex: 'url',
      key: 'url',
      render: (url: string, record: DocumentItem) =>
        url ? (
          <Button
            type="link"
            size="small"
            loading={loadingDocId === record.id}
            onClick={() => handleView(record.id)}
          >
            Voir
          </Button>
        ) : (
          '—'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: DocumentItem) => (
        <Space>
          {record.status !== 'VALIDATED' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={() => handleValidate(record.id)}
              loading={validateMutation.isPending}
            >
              Valider
            </Button>
          )}
          {record.status !== 'DENIED' && (
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={() => {
                setDenyDocId(record.id);
                setDenyModalOpen(true);
              }}
            >
              Refuser
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={handleBatchValidate} loading={batchValidateMutation.isPending}>
            Valider la sélection ({selectedRowKeys.length})
          </Button>
        </div>
      )}
      <Table
        columns={columns}
        dataSource={documents}
        loading={isLoading}
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          getCheckboxProps: (record: DocumentItem) => ({
            disabled: record.status === 'VALIDATED',
          }),
        }}
        pagination={false}
        size="small"
      />
      <Modal
        title="Refuser le document"
        open={denyModalOpen}
        onOk={handleDeny}
        onCancel={() => {
          setDenyModalOpen(false);
          setDenyReason('');
          setDenyDocId(null);
        }}
        okText="Refuser"
        cancelText="Annuler"
        okButtonProps={{ danger: true, disabled: !denyReason.trim() }}
      >
        <Input.TextArea
          rows={3}
          placeholder="Raison du refus (obligatoire)..."
          value={denyReason}
          onChange={(e) => setDenyReason(e.target.value)}
        />
      </Modal>

      <Modal
        title="Aperçu du document"
        open={!!previewDisplayUrl}
        onCancel={handleClosePreview}
        footer={
          previewOriginalUrl ? (
            <Button type="link" href={previewOriginalUrl} target="_blank" rel="noopener noreferrer">
              Ouvrir dans un nouvel onglet
            </Button>
          ) : null
        }
        width={900}
        styles={{ body: { padding: 0, textAlign: 'center', minHeight: 400 } }}
        destroyOnClose
      >
        {previewDisplayUrl && previewType === 'image' && (
          <img
            src={previewDisplayUrl}
            alt="Document"
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        )}
        {previewDisplayUrl && previewType === 'pdf' && (
          <iframe
            src={previewDisplayUrl}
            title="Document PDF"
            style={{ width: '100%', height: '80vh', border: 'none' }}
          />
        )}
        {previewDisplayUrl && previewType === 'other' && (
          <div style={{ padding: 40 }}>
            <p>Ce type de fichier ne peut pas être prévisualisé.</p>
            <Button type="primary" href={previewOriginalUrl ?? previewDisplayUrl} target="_blank" rel="noopener noreferrer">
              Télécharger le document
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
}
